import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { ToggleGroup } from '@/components/Form/ToggleGroup';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';

import { getDataTestId } from '@/utils/browser';
import { isNil } from '@/utils/common';
import { getForeignIndiciaCountries } from '@/utils/user';

import { ForeignNonTaxResidencySection } from '../ForeignNonTaxResidencySection/ForeignNonTaxResidencySection';
import { ForeignTaxResidencySection } from '../ForeignTaxResidencySection/ForeignTaxResidencySection';
import { OtherCountriesForeignIndiciaSection } from '../OtherCountriesForeignIndiciaSection/OtherCountriesForeignIndiciaSection';
import { TaxQuestionProps } from '../TaxInfoDrawer.interface';
import { TaxInfoQuestionForm } from '../TaxInfoQuestionForm';
import { useOtherCountriesTaxStatusQuestionForm } from './useOtherCountriesTaxStatusQuestionForm';

import {
  TaxInfoQuestionLabel,
  TaxInfoQuestions,
  TaxInfoQuestionSection,
} from '../TaxInfoDrawer.styled';

export function QuestionOtherCountriesTaxStatus({
  index,
  total,
  onAnswered,
}: Readonly<TaxQuestionProps>) {
  const { t, country: translatedCountry } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const { isoCode: tenantCountryCode } = useAppSelector(selectConfigsState);

  const questionCountryParams = useMemo(
    () => ({
      tenantCountry: translatedCountry[tenantCountryCode] ?? '',
    }),
    [tenantCountryCode, translatedCountry],
  );

  const foreignIndiciaCountries = useMemo(
    () => getForeignIndiciaCountries(user, tenantCountryCode),
    [user, tenantCountryCode],
  );

  const {
    control,
    errors,
    canSubmit,
    isPending,
    error,
    onSubmit,
    resetField,
    showForeignIndiciaWarning,
    showForeignTaxResidencyForm,
    showForeignNonTaxResidencyForm,
  } = useOtherCountriesTaxStatusQuestionForm({
    foreignIndiciaCountries,
    tenantCountryCode,
    onAnswered,
  });

  return (
    <TaxInfoQuestionForm
      index={index}
      total={total}
      sectionTitle={t('taxInfoDrawer.question2.sectionTitle')}
      answerError={error}
      isLoading={isPending}
      onSubmit={onSubmit}
    >
      <TaxInfoQuestionSection>
        <TaxInfoQuestions>
          <TaxInfoQuestionLabel variant="body1Medium">
            {t(
              'taxInfoDrawer.question2.foreignIndicia.question',
              questionCountryParams,
            )}
          </TaxInfoQuestionLabel>
          <Controller
            control={control}
            name="hasForeignIndicia"
            render={({ field: { value, onChange } }) => (
              <ToggleGroup
                fullWidth
                size="medium"
                name="hasForeignIndicia"
                options={[
                  { value: 'false', text: t('common.no') },
                  { value: 'true', text: t('common.yes') },
                ]}
                value={isNil(value) ? null : String(value)}
                onChange={(_event, selectedValue) => {
                  if (selectedValue !== null) {
                    onChange(selectedValue === 'true');
                  }
                }}
                {...getDataTestId('tax-info-foreign-indicia-toggle')}
              />
            )}
          />
          <TaxInfoQuestionLabel variant="body1Medium">
            {t(
              'taxInfoDrawer.question2.foreignTaxResident.question',
              questionCountryParams,
            )}
          </TaxInfoQuestionLabel>
          <Controller
            control={control}
            name="isForeignTaxResident"
            render={({ field: { value, onChange } }) => (
              <ToggleGroup
                fullWidth
                size="medium"
                name="isForeignTaxResident"
                options={[
                  { value: 'false', text: t('common.no') },
                  { value: 'true', text: t('common.yes') },
                ]}
                value={isNil(value) ? null : String(value)}
                onChange={(_event, selectedValue) => {
                  if (selectedValue !== null) {
                    onChange(selectedValue === 'true');
                  }
                }}
                {...getDataTestId('tax-info-foreign-tax-resident-toggle')}
              />
            )}
          />
        </TaxInfoQuestions>
      </TaxInfoQuestionSection>
      {showForeignIndiciaWarning && (
        <OtherCountriesForeignIndiciaSection
          control={control}
          errors={errors}
          foreignIndiciaCountries={foreignIndiciaCountries}
          isPending={isPending}
          resetField={resetField}
        />
      )}
      {showForeignNonTaxResidencyForm && (
        <ForeignNonTaxResidencySection
          control={control}
          errors={errors}
          isPending={isPending}
          resetField={resetField}
          tenantCountryCode={tenantCountryCode}
        />
      )}
      {showForeignTaxResidencyForm && (
        <ForeignTaxResidencySection
          control={control}
          errors={errors}
          isPending={isPending}
          resetField={resetField}
          tenantCountryCode={tenantCountryCode}
        />
      )}

      <LoadingButton
        {...getDataTestId('tax-info-question-continue-button')}
        type="submit"
        variant="contained"
        fullWidth
        isLoading={isPending}
        disabled={!canSubmit || isPending}
      >
        {t('common.continue')}
      </LoadingButton>
    </TaxInfoQuestionForm>
  );
}
